import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../lib/cn';

const gridVariants = cva('grid', {
	variants: {
		/** Column count; `responsive` variants collapse to 1 column on small screens. */
		columns: {
			1: 'grid-cols-1',
			2: 'grid-cols-2',
			3: 'grid-cols-3',
			4: 'grid-cols-4',
			6: 'grid-cols-6',
			12: 'grid-cols-12',
			responsive2: 'grid-cols-1 md:grid-cols-2',
			responsive3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
			responsive4: 'grid-cols-2 lg:grid-cols-4',
			/** Container-query: 2 → 3 → 5 columns as the panel widens. */
			responsive5: 'grid-cols-2 @sm:grid-cols-3 @lg:grid-cols-5',
			/**
			 * Come `responsive5` ma con soglie più larghe (`@lg`/`@4xl` invece di
			 * `@sm`/`@lg`): per celle con testo più lungo (es. "Autoconsumo", "CO₂
			 * Risparmiata"), dove i passaggi di `responsive5` arrivano troppo tardi
			 * e il titolo va in overflow prima di guadagnare una colonna in meno.
			 */
			kpi5: 'grid-cols-2 @lg:grid-cols-3 @4xl:grid-cols-5',
		},
		gap: {
			none: 'gap-0',
			xxs: 'gap-1',
			xs: 'gap-1.5',
			sm: 'gap-2',
			md: 'gap-3',
			lg: 'gap-4',
			xl: 'gap-5',
		},
		align: {
			start: 'items-start',
			center: 'items-center',
			end: 'items-end',
			stretch: 'items-stretch',
		},
	},
	defaultVariants: {
		columns: 2,
		gap: 'md',
	},
});

export interface GridProps extends React.HTMLAttributes<HTMLDivElement> {
	/** Numero di colonne (o preset responsive `responsiveN` che collassano su schermi piccoli). */
	columns?: VariantProps<typeof gridVariants>['columns'];
	/** Spaziatura tra le celle. */
	gap?: VariantProps<typeof gridVariants>['gap'];
	/** Allineamento verticale del contenuto di ogni cella. */
	align?: VariantProps<typeof gridVariants>['align'];
}

/** Contenitore a griglia CSS per layout a colonne (dashboard, form, liste di card). Usare insieme a {@link GridItem} per far occupare più colonne a un figlio. */
const Grid = React.forwardRef<HTMLDivElement, GridProps>(
	({ className, columns, gap, align, ...props }, ref) => (
		<div ref={ref} className={cn(gridVariants({ columns, gap, align, className }))} {...props} />
	),
);
Grid.displayName = 'Grid';

const gridItemVariants = cva('min-w-0', {
	variants: {
		span: {
			1: 'col-span-1',
			2: 'col-span-2',
			3: 'col-span-3',
			4: 'col-span-4',
			6: 'col-span-6',
			8: 'col-span-8',
			12: 'col-span-12',
			full: 'col-span-full',
		},
	},
});

export interface GridItemProps extends React.HTMLAttributes<HTMLDivElement> {
	/** Quante colonne della {@link Grid} genitore deve occupare (o `full` per tutta la riga). */
	span?: VariantProps<typeof gridItemVariants>['span'];
}

/** Cella figlia di {@link Grid} che occupa più colonne tramite `span`. */
const GridItem = React.forwardRef<HTMLDivElement, GridItemProps>(
	({ className, span, ...props }, ref) => (
		<div ref={ref} className={cn(gridItemVariants({ span, className }))} {...props} />
	),
);
GridItem.displayName = 'GridItem';

export { Grid, GridItem, gridItemVariants, gridVariants };
